// The head of each page (decision 136): the title, the description, the
// canonical address and whether search engines may index it. The public
// pages (the introduction, the guide, the privacy notice) are described and
// canonical on sharifly.com; every page behind a sign-in is kept out of the
// index. The prerender writes the same values into the static HTML of the
// public pages; in the browser the values follow the route.

import { useEffect } from "react";

/** The platform's public address: the canonical host of every page, whatever name served it. */
export const SITE_URL = "https://sharifly.com";

export type PageHead = {
  title: string;
  description: string;
  /** The canonical address, absolute; null keeps the page out of the index. */
  canonical: string | null;
  robots: string;
};

const SITE_TITLE = "ShariFly: shared aircraft and fleet management for X-Plane 12";
const SITE_DESCRIPTION =
  "Several pilots, one aircraft, one logbook. Book it, fly it in X-Plane 12, return it as it is; the platform keeps the hours, the landings, the wear and the live map.";

/** The public pages and their words; everything else is private. */
export const PUBLIC_PAGES: Record<string, { title: string; description: string }> = {
  "/": { title: SITE_TITLE, description: SITE_DESCRIPTION },
  "/guide": {
    title: "Getting started with ShariFly",
    description: "Install the app and the plugin, sign in with Discord, and book your first shared aircraft in X-Plane 12. Three parts, installed once.",
  },
  "/privacy": {
    title: "How your data is kept at ShariFly",
    description: "Plain words on what ShariFly keeps of you and of the aircraft you fly, who sees it, and how to take it away.",
  },
};

/** The head of a page: a public one by its words, any other kept out of the index. */
export function pageHead(pathname: string, signedIn: boolean): PageHead {
  const path = pathname.replace(/\/+$/, "") || "/";
  const page = PUBLIC_PAGES[path];
  if (page === undefined || signedIn) {
    return { title: page?.title ?? "ShariFly", description: page?.description ?? SITE_DESCRIPTION, canonical: null, robots: "noindex, nofollow" };
  }
  return { title: page.title, description: page.description, canonical: `${SITE_URL}${path === "/" ? "/" : path}`, robots: "index, follow, max-image-preview:large" };
}

function meta(doc: Document, selector: string, make: () => HTMLElement): HTMLElement {
  const found = doc.head.querySelector<HTMLElement>(selector);
  if (found) return found;
  const el = make();
  doc.head.appendChild(el);
  return el;
}

/** Writes a head into a document: the title, the description, the robots rule, the canonical link (removed when none). */
export function applyHead(doc: Document, head: PageHead): void {
  doc.title = head.title;
  meta(doc, 'meta[name="description"]', () => {
    const m = doc.createElement("meta");
    m.setAttribute("name", "description");
    return m;
  }).setAttribute("content", head.description);
  meta(doc, 'meta[name="robots"]', () => {
    const m = doc.createElement("meta");
    m.setAttribute("name", "robots");
    return m;
  }).setAttribute("content", head.robots);
  for (const [property, value] of [
    ["og:title", head.title],
    ["og:description", head.description],
    ["og:url", head.canonical ?? `${SITE_URL}/`],
  ] as const) {
    meta(doc, `meta[property="${property}"]`, () => {
      const m = doc.createElement("meta");
      m.setAttribute("property", property);
      return m;
    }).setAttribute("content", value);
  }
  const canonical = doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (head.canonical === null) {
    canonical?.remove();
  } else {
    const link =
      canonical ??
      (() => {
        const l = doc.createElement("link");
        l.setAttribute("rel", "canonical");
        doc.head.appendChild(l);
        return l;
      })();
    link.setAttribute("href", head.canonical);
  }
}

/** Keeps the document's head in step with the route and the session. */
export function usePageHead(pathname: string, signedIn: boolean, pending: boolean): void {
  useEffect(() => {
    if (pending) return;
    applyHead(document, pageHead(pathname, signedIn));
  }, [pathname, signedIn, pending]);
}
